import { useLingui } from '@lingui/react/macro';
import { t } from '@lingui/macro';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const SERVICE_OPTIONS = [
  { value: 'ALL', label: 'All Services' },
  { value: 'FULL_DAY', label: 'Full Day' },
  { value: 'MORNING', label: 'Morning' },
  { value: 'LUNCH', label: 'Lunch' },
  { value: 'AFTERNOON', label: 'Afternoon' },
  { value: 'LATE', label: 'Late' },
];

export const DAY_OPTIONS = [
  { value: 'ALL', label: 'All Days' },
  { value: 'WEEKDAYS', label: 'Weekdays' },
  { value: 'WEEKENDS', label: 'Weekends' },
  { value: 'MONDAY', label: 'Monday', isWeekday: true },
  { value: 'TUESDAY', label: 'Tuesday', isWeekday: true },
  { value: 'WEDNESDAY', label: 'Wednesday', isWeekday: true },
  { value: 'THURSDAY', label: 'Thursday', isWeekday: true },
  { value: 'FRIDAY', label: 'Friday', isWeekday: true },
  { value: 'SATURDAY', label: 'Saturday', isWeekday: true },
  { value: 'SUNDAY', label: 'Sunday', isWeekday: true },
];

export function getDayNumbers(selection: string): number[] | undefined {
  switch (selection) {
    case 'ALL':
      return undefined;
    case 'WEEKDAYS':
      return [1, 2, 3, 4, 5];
    case 'WEEKENDS':
      return [6, 7];
    case 'MONDAY':
      return [1];
    case 'TUESDAY':
      return [2];
    case 'WEDNESDAY':
      return [3];
    case 'THURSDAY':
      return [4];
    case 'FRIDAY':
      return [5];
    case 'SATURDAY':
      return [6];
    case 'SUNDAY':
      return [7];
    default:
      return undefined;
  }
}

interface TariffBulkSelectionPanelProps {
  selectedService: string;
  selectedDays: string;
  onServiceChange: (value: string) => void;
  onDaysChange: (value: string) => void;
  isDisabled?: boolean;
}

export function TariffBulkSelectionPanel({
  selectedService,
  selectedDays,
  onServiceChange,
  onDaysChange,
  isDisabled = false,
}: TariffBulkSelectionPanelProps) {
  const { i18n } = useLingui();

  // Helper function to get translated day label
  const getDayLabel = (opt: typeof DAY_OPTIONS[0]) => {
    if (!opt.isWeekday) {
      return opt.label;
    }

    switch (opt.value) {
      case 'MONDAY':
        return i18n._({ id: 'common.days.monday', message: 'Monday' });
      case 'TUESDAY':
        return i18n._({ id: 'common.days.tuesday', message: 'Tuesday' });
      case 'WEDNESDAY':
        return i18n._({ id: 'common.days.wednesday', message: 'Wednesday' });
      case 'THURSDAY':
        return i18n._({ id: 'common.days.thursday', message: 'Thursday' });
      case 'FRIDAY':
        return i18n._({ id: 'common.days.friday', message: 'Friday' });
      case 'SATURDAY':
        return i18n._({ id: 'common.days.saturday', message: 'Saturday' });
      case 'SUNDAY':
        return i18n._({ id: 'common.days.sunday', message: 'Sunday' });
      default:
        return opt.label;
    }
  };

  return (
    <>
      <div>
        <Label htmlFor="service-select">{i18n._(t`Service`)}</Label>
        <Select
          value={selectedService}
          onValueChange={onServiceChange}
          disabled={isDisabled}
        >
          <SelectTrigger id="service-select" aria-label={i18n._(t`Select service`)}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="days-select">{i18n._(t`Days`)}</Label>
        <Select
          value={selectedDays}
          onValueChange={onDaysChange}
          disabled={isDisabled}
        >
          <SelectTrigger id="days-select" aria-label={i18n._(t`Select days`)}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAY_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {getDayLabel(opt)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}